class CartPriorityProcessingRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.cartItems = null; // Initialize as null
    // Bind the 'this' context to the event handlers
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    // Find the nested button element
    const button = this.querySelector("button");

    // Attach event listener for the click
    button.addEventListener("click", this.handleClick);
  }

  disconnectedCallback() {
    // Find the nested button and remove event listener
    const button = this.querySelector("button");
    button.removeEventListener("click", this.handleClick);
  }

  // Handle button click
  async handleClick() {
    this.cartItems = this._getClosestCartItems();
    const itemIndex = this.getAttribute("data-index");

    this.cartItems.updateQuantity(itemIndex, 0);
  }

  _getClosestCartItems() {
    // Start by finding the closest common ancestor
    let commonAncestor = this.closest(".drawer__inner");

    // From the common ancestor, find 'cart-drawer-items'
    if (commonAncestor) {
      return commonAncestor.querySelector("cart-drawer-items");
    }

    return null;
  }
}

// Register the custom element
customElements.define(
  "cart-priority-proccessing-remove-button",
  CartPriorityProcessingRemoveButton
);

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this._initEventListeners();
    this.cartItems = null; // Initialize as null
  }

  _initEventListeners() {
    this.addEventListener("click", this._handleClick.bind(this));
  }

  _handleClick(event) {
    event.preventDefault();
    this.cartItems = this._getClosestCartItems();
    if (!this.cartItems) return;

    const cartItemKey = this.dataset.cartItemKey || null;
    /* 
        If this attribute exists, it means that this item depends on another, and if it's removed
        the other item will also be removed
     */
    const relatedCartItemKey = this.dataset.relatedItemKey || null;

    // Check if there's any engraving service item to remove
    const engravingServiceKey = this._getEngravingServiceKey(cartItemKey);

    // Check if there's any protection plan service item to remove
    const protectionPlanKey = this._getProtectionPlanKey(cartItemKey);

    // Prepare an array of keys to remove
    const keysToRemove = [
      cartItemKey,
      engravingServiceKey,
      protectionPlanKey,
      relatedCartItemKey,
    ].filter(Boolean);

    // Call the _batchRemoveItems method from cartItems
    this.cartItems.batchRemoveItems(keysToRemove);
  }

  _getClosestCartItems() {
    return this.closest("cart-items") || this.closest("cart-drawer-items");
  }

  _getEngravingServiceKey(cartItemKey) {
    const engravingService = this.cartItems.querySelector(
      `[data-engraving-service-related-product-key="${cartItemKey}"]`
    );
    return engravingService
      ? engravingService.dataset.engravingServiceKey
      : null;
  }

  _getProtectionPlanKey(cartItemKey) {
    const protectionPlan = this.cartItems.querySelector(
      `remove-accident-plan-button[data-cart-item-key="${cartItemKey}"]`
    );
    return protectionPlan ? protectionPlan.dataset.protectionPlanKey : null;
  }
}

// Register the custom element
customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }
        this.onCartUpdate();
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute("name"),
      event.target.dataset.quantityVariantId
    );
  }

  onCartUpdate() {
    if (this.tagName === "CART-DRAWER-ITEMS") {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const selectors = [
            "cart-drawer-items",
            ".cart-drawer__footer",
            "priority-proccesing-line-item",
          ];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              if (selector === "cart-drawer-items") {
                const sourceElementCartItems =
                  sourceElement.querySelectorAll(".cart-item");
                // Shopify.GlobalEngravingOptions.checkLineItemInEngravingOptionsList(
                //   sourceElementCartItems
                // );
              }
              targetElement.replaceWith(sourceElement);
            }
          }
          // We send the most recent cart info in the event to extend the cart functionality like the section-gift-with-purchase.liquid
          let cartParsedData = null;
          const cartScript = html.getElementById("cart-json-data");
          if (cartScript) {
            cartParsedData = JSON.parse(cartScript.textContent);
          }
          // Update all cart-item-count elements
          const cartItemCountElems =
            document.querySelectorAll("[cart-item-count]");
          if (cartItemCountElems.length > 0) {
            cartItemCountElems.forEach((cartItemCount) => {
              // Update the text content
              cartItemCount.textContent = cartParsedData.item_count;
            });
          }
          // Trigger an event when the cart drawer DOM has been updated
          publish(PUB_SUB_EVENTS.cartDrawerDOMUpdated, {
            source: "cart-drawer",
            action: "cart-drawer-items",
            cartData: cartParsedData,
          });
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.validatePromotionDiscountOnSecondProduct();
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const sourceQty = html.querySelector("cart-items");
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.validatePromotionDiscountOnSecondProduct();
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer").dataset.id,
        selector: ".js-contents",
      },
    ];
  }

  toggleCartEmptyState(cartItems, parsedState, cartDrawerWrapper, cartFooter) {
    cartItems.classList.toggle("is-empty", parsedState.item_count === 0);
    if (cartFooter)
      cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
    if (cartDrawerWrapper)
      cartDrawerWrapper.classList.toggle(
        "is-empty",
        parsedState.item_count === 0
      );
  }

  batchRemoveItems(keysToRemove) {
    // Initialize an empty updates object
    const updates = {};

    // Populate updates object based on the keys to remove
    keysToRemove.forEach((key) => {
      if (key) updates[key] = 0;
    });

    // Create the request body
    const body = JSON.stringify({
      updates,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    // Perform the fetch operation
    fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
    })
      .then((response) => response.json())
      .then((parsedState) => {
        // Toggle cart empty state and update cart sections
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartFooter = document.getElementById("main-cart-footer");
        this.toggleCartEmptyState(
          this,
          parsedState,
          cartDrawerWrapper,
          cartFooter
        );

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        // Publish a cart update event
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-remove-button",
          cartData: parsedState,
        });
      })
      .catch((e) => {
        console.error(e);
      });
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartFooter = document.getElementById("main-cart-footer");
        this.toggleCartEmptyState(
          this,
          parsedState,
          cartDrawerWrapper,
          cartFooter
        );

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = "";
        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === "undefined") {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              "[quantity]",
              updatedValue
            );
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`)
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector(".drawer__inner-empty"),
            cartDrawerWrapper.querySelector("a")
          );
        } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector(".cart-item__name")
          );
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          cartData: parsedState,
          variantId: variantId,
        });

        // Trigger an event when the cart drawer DOM has been updated
        publish(PUB_SUB_EVENTS.cartDrawerDOMUpdated, {
          source: "cart-items",
          action: "updateQuantity",
          cartData: parsedState,
        });
      })
      .catch((e) => {
        this.querySelectorAll(".loading-overlay").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item__error-text").innerHTML = message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.add("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden")
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.remove("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add("hidden")
    );
  }

  /**
   * Validates that the promotion is only applied
   * if a "Watch" product exists in the cart.
   * If no "Watch" products are found, the promotional product
   * wil be removed.
   *
   * @returns {void} If the cart is empty exits early.
   */
  async validatePromotionDiscountOnSecondProduct() {
    // Fetch the current cart data
    const cartRequest = await fetch("/cart.js");
    const cartResponse = await cartRequest.json();
    const { item_count, items } = cartResponse;

    // If cart is empty, exit early
    if (item_count === 0) return;

    /**
     * Check if there are any "Watch" products in the cart that are
     * not associated with the specific promotion (_widget_id !== "105394").
     */
    const areWatchProductsInCart = items.some(
      (item) =>
        item.product_type.toLowerCase().indexOf("watch") > -1 &&
        item?.properties?._widget_id !== "105394"
    );

    /**
     * Check if there are promotional products (identified by _widget_id === "105394")
     * is in the cart.
     */
    const arePromotionProductsInCart = items.some(
      (item) => item?.properties._widget_id === "105394"
    );

    /**
     * If there are no "Watch" products in the cart, but a promotional product
     * is present, proceed to remove the promotional product from the cart.
     */
    if (!areWatchProductsInCart && arePromotionProductsInCart) {
      const promotionProduct = items.find(
        (item) => item.properties._widget_id === "105394"
      );

      const { key } = promotionProduct;

      this.batchRemoveItems([key]);
    }
  }
}

customElements.define("cart-items", CartItems);

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "change",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
